import { useState, useCallback } from 'react';
import { getManagerProfile, updateManagerProfile } from '../../../../../insfrastructure/services/manager-profile';
import { getCompanyProfile, updateCompanyProfile, convertSocialMediaFromAPI } from '../../../../../insfrastructure/services/company-profile';
import { AUTH_CONSTANTS } from '../../../../../insfrastructure/utils/auth-constants';
import { ManagerProfile, CompanyProfile } from '../types';

export const useManagerProfile = () => {
  const [managerProfile, setManagerProfile] = useState<ManagerProfile>({
    full_name: '',
    cpf: '',
    manager_email: '',
    date_of_birth: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);

  const loadManagerProfile = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const managerId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID);
      
      if (!managerId) {
        throw new Error('Manager ID não encontrado');
      }

      const managerData = await getManagerProfile(managerId);
      
      setManagerProfile({
        full_name: managerData.full_name || '',
        cpf: managerData.cpf || '',
        manager_email: managerData.manager_email || '',
        date_of_birth: managerData.date_of_birth || '',
        gender: managerData.gender || ''
      });
    } catch (error: any) {
      console.error('Erro ao carregar dados do manager:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateManager = useCallback(async (field: string, value: any): Promise<ManagerProfile> => {
    const managerId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.MANAGER_ID);
    if (!managerId) {
      throw new Error('Manager ID não encontrado');
    }

    const updateData: any = {};
    updateData[field] = value;
    
    const updatedManager = await updateManagerProfile(managerId, updateData);
    
    const updatedProfile: ManagerProfile = {
      full_name: updatedManager.full_name || '',
      cpf: updatedManager.cpf || '',
      manager_email: updatedManager.manager_email || '',
      date_of_birth: updatedManager.date_of_birth || '',
      gender: updatedManager.gender || ''
    };
    
    setManagerProfile(updatedProfile);
    return updatedProfile;
  }, []);

  return { managerProfile, loading, loadManagerProfile, updateManager };
};

export const useCompanyProfile = () => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>({
    logo: null,
    legal_name: '',
    name: '',
    fantasy_name: '',
    cnpj: '',
    company_email: '',
    phone: '',
    website: '',
    postcode: '',
    address: '',
    address_number: '',
    address_complement: '',
    address_district: '',
    city_name: '',
    uf: '',
    segment_name: '',
    segment: '',
    segment_id: '',
    social_media: [],
    description: '',
    motivation: ''
  });
  const [loading, setLoading] = useState(false);

  const mapCompanyDataFromAPI = (companyData: any): CompanyProfile => {
    const socialMedia = convertSocialMediaFromAPI(companyData);
    
    return {
      logo: null,
      legal_name: companyData.name || '',
      name: companyData.name || '',
      fantasy_name: companyData.name || '',
      cnpj: companyData.cnpj || '',
      company_email: companyData.email || '',
      phone: companyData.phone || '',
      postcode: companyData.postcode || '',
      // Montar endereço completo se não estiver montado: "Rua, Bairro, Cidade, UF"
      address: (() => {
        const currentAddress = companyData.address || '';
        const trimmedAddress = currentAddress.trim();
        
        // Validar se o endereço está no formato correto usando regex dinâmica
        // Aceita qualquer quantidade de partes (mínimo 2), desde que:
        // - Cada parte tenha pelo menos 1 caractere não-whitespace
        // - Não tenha vírgulas duplicadas ou partes vazias
        // Regex: ^[^,]+(?:,\s*[^,]+)+$
        //   ^[^,]+        - Primeira parte (pelo menos 1 caractere não-vírgula)
        //   (?:,\s*[^,]+)+ - Uma ou mais partes adicionais (vírgula + espaços opcionais + conteúdo)
        //   $              - Fim da string
        const isValidAddressFormat = /^[^,]+(?:,\s*[^,]+)+$/.test(trimmedAddress);
        
        if (isValidAddressFormat) {
          return currentAddress;
        }
        
        // Caso contrário, montar a partir dos campos individuais
        const addressParts = [];
        if (currentAddress && !currentAddress.includes(',')) {
          // Se não tem vírgula, pode ser apenas a rua
          addressParts.push(currentAddress);
        }
        if (companyData.address_district) addressParts.push(companyData.address_district);
        if (companyData.city) addressParts.push(companyData.city);
        if (companyData.uf) addressParts.push(companyData.uf);
        return addressParts.length > 0 ? addressParts.join(', ') : '';
      })(),
      address_number: companyData.address_number || '',
      address_complement: companyData.address_complement || '',
      address_district: companyData.address_district || '',
      city_name: companyData.city || '',
      uf: companyData.uf || '',
      segment_name: companyData.segment?.name || '',
      segment: companyData.segment?.name || '',
      segment_id: String(companyData.segment_id || companyData.segment?.id || ''),
      website: companyData.website || '',
      social_media: socialMedia,
      description: companyData.description ?? '',
      motivation: companyData.motivation ?? '',
      city_id: companyData.city_id || ''
    };
  };

  const loadCompanyProfile = useCallback(async (): Promise<CompanyProfile | null> => {
    try {
      setLoading(true);
      const companyId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID);
      
      if (!companyId) {
        return null;
      }

      const companyData = await getCompanyProfile(companyId);
      const mappedCompany = mapCompanyDataFromAPI(companyData);
      setCompanyProfile(mappedCompany);
      return mappedCompany;
    } catch (error: any) {
      console.error('Erro ao carregar dados da empresa:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCompany = useCallback(async (updateData: any): Promise<CompanyProfile> => {
    const companyId = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.COMPANY_ID);
    if (!companyId) {
      throw new Error('Company ID não encontrado');
    }

    const updatedCompany = await updateCompanyProfile(companyId, updateData);
    const mappedCompany = mapCompanyDataFromAPI(updatedCompany);
    setCompanyProfile(mappedCompany);
    return mappedCompany;
  }, []);

  return { 
    companyProfile, 
    loading, 
    loadCompanyProfile, 
    updateCompany,
    mapCompanyDataFromAPI 
  };
};

